/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/modular-form/components/CustomerQRSection.tsx
 * Release: v2.0.0 - Layout compatto con modale per QR code
 * Data: 2026-01-17
 * Descrizione: Mostra QR code univoco del cliente in modale con pulsante per stampare
 */

import React, { useRef, useState } from 'react';
import { QrCode, Printer, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import QRCodeWithLogo from '../../ui/QRCodeWithLogo/QRCodeWithLogo';

interface CustomerQRSectionProps {
  customerCode: string | null | undefined;
  customerName?: string;
  className?: string;
}

export const CustomerQRSection: React.FC<CustomerQRSectionProps> = ({
  customerCode,
  customerName,
  className = ''
}) => {
  const printRef = useRef<HTMLDivElement>(null);
  const [showQRModal, setShowQRModal] = useState(false);

  // Se non c'è codice cliente, non mostrare nulla
  if (!customerCode) {
    return null;
  }

  const qrValue = `CORONATEK-${customerCode}`;

  // Funzione per stampare il QR code
  const handlePrint = () => {
    // Estrai SVG dal ref nascosto
    const qrSvgElement = printRef.current?.querySelector('svg');
    const qrSvgString = qrSvgElement
      ? new XMLSerializer().serializeToString(qrSvgElement)
      : '';

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>QR Code Cliente - ${customerName || customerCode}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          @page { margin: 15mm; }
          html, body {
            font-family: Arial, sans-serif;
          }
          body {
            display: flex;
            justify-content: center;
            padding-top: 30px;
          }
          .qr-container {
            text-align: center;
            width: 220px;
          }
          .customer-name {
            font-size: 14px;
            font-weight: bold;
            margin-bottom: 15px;
            word-wrap: break-word;
          }
          .qr-image {
            display: flex;
            justify-content: center;
            margin-bottom: 12px;
          }
          .qr-image svg {
            width: 200px;
            height: 200px;
          }
          .customer-code {
            font-size: 18px;
            font-family: monospace;
            font-weight: bold;
            margin-bottom: 8px;
          }
          .logo-text {
            font-size: 11px;
            color: #666;
          }
        </style>
      </head>
      <body>
        <div class="qr-container">
          ${customerName ? `<div class="customer-name">${customerName}</div>` : ''}
          <div class="qr-image">${qrSvgString}</div>
          <div class="customer-code">${customerCode}</div>
          <div class="logo-text">CoronaTek</div>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    }
  };

  return (
    <>
      {/* Layout compatto: Label cliccabile + Pulsante Stampa */}
      <div className={`flex items-center justify-between ${className}`}>
        {/* Lato sinistro: Label cliccabile per aprire modale */}
        <button
          type="button"
          onClick={() => setShowQRModal(true)}
          className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-zinc-700/50 transition-colors group"
        >
          <div className="p-2 bg-blue-600 rounded-lg group-hover:bg-blue-500 transition-colors">
            <QrCode className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <h4 className="font-semibold text-white text-sm group-hover:text-blue-300 transition-colors">
              QR Code Cliente
            </h4>
            <p className="text-xs text-zinc-400 font-mono">{customerCode}</p>
          </div>
        </button>

        {/* Lato destro: Pulsante Stampa */}
        <button
          type="button"
          onClick={handlePrint}
          className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium text-sm shadow-lg shadow-green-600/20"
        >
          <Printer className="w-4 h-4" />
          Stampa QR
        </button>
      </div>

      {/* QR nascosto per stampa */}
      <div className="hidden" ref={printRef}>
        <QRCodeWithLogo
          value={qrValue}
          size={150}
          customerCode={customerCode}
          downloadable={false}
        />
      </div>

      {/* Modale QR Code */}
      <AnimatePresence>
        {showQRModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm"
            onClick={() => setShowQRModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-zinc-800 rounded-2xl shadow-2xl border border-zinc-700 max-w-sm w-full mx-4 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header modale */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-700 bg-zinc-900/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-600 rounded-lg">
                    <QrCode className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">QR Code Cliente</h3>
                    <p className="text-xs text-zinc-400 font-mono">{customerCode}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowQRModal(false)}
                  className="p-2 hover:bg-zinc-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-zinc-400" />
                </button>
              </div>

              {/* Contenuto: QR Code */}
              <div className="p-6 flex flex-col items-center">
                {customerName && (
                  <p className="text-white font-medium mb-4 text-center">{customerName}</p>
                )}
                <div className="bg-white p-4 rounded-xl shadow-lg">
                  <QRCodeWithLogo
                    value={qrValue}
                    size={200}
                    customerCode={customerCode}
                    downloadable={false}
                  />
                </div>
                <p className="mt-4 text-sm text-zinc-400 text-center">
                  Scansiona questo QR code per identificare il cliente
                </p>
              </div>

              {/* Footer con pulsanti */}
              <div className="px-5 py-4 border-t border-zinc-700 bg-zinc-900/50 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowQRModal(false)}
                  className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  Chiudi
                </button>
                <button
                  type="button"
                  onClick={() => {
                    handlePrint();
                    setShowQRModal(false);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  <Printer className="w-4 h-4" />
                  Stampa
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CustomerQRSection;
